import React, { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFavorites } from '../hooks/useLocalStorage';
import ShopCard from '../components/user/ShopCard';
import Loading from '../components/common/Loading';
import { Heart, CheckCircle, XCircle, HelpCircle } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   FILTRES
───────────────────────────────────────────────────────────────── */
const FILTERS = [
  { key: 'all',       label: 'Tous',       dot: null,      icon: null         },
  { key: 'open',      label: 'Ouverts',    dot: '#00c9a7', icon: CheckCircle  },
  { key: 'closed',    label: 'Fermés',     dot: '#f0564a', icon: XCircle      },
  { key: 'uncertain', label: 'Incertains', dot: '#f5a623', icon: HelpCircle   },
];

/* ─────────────────────────────────────────────────────────────────
   PAGE FAVORIS
───────────────────────────────────────────────────────────────── */
const Favorites = () => {
  const [allShops, setAllShops] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const { favorites }           = useFavorites();

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'shops'),
      snap => { setAllShops(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      ()   => setLoading(false)
    );
    return () => unsub();
  }, []);

  const favShops = useMemo(
    () => allShops.filter(s => favorites.includes(s.id)),
    [allShops, favorites]
  );

  /* Compteurs par statut */
  const counts = useMemo(() => ({
    open:      favShops.filter(s => s.status === 'open').length,
    closed:    favShops.filter(s => s.status === 'closed').length,
    uncertain: favShops.filter(s => s.status === 'uncertain').length,
  }), [favShops]);

  /* Liste filtrée */
  const displayed = useMemo(() => {
    if (filter === 'all') return favShops;
    return favShops.filter(s => s.status === filter);
  }, [favShops, filter]);

  if (loading) return <Loading message="Chargement des favoris…" />;

  return (
    <div className="page-enter">

      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <div className="fav-hero">

        {/* Sur-titre + badge */}
        <div className="fav-hero__top">
          <p className="fav-hero__label">Mes sauvegardes</p>
          {favShops.length > 0 && (
            <span className="fav-hero__badge">{favShops.length}</span>
          )}
        </div>

        {/* Titre avec icône */}
        <h2 className="fav-hero__title">
 
          Favoris
        </h2>

        {/* Sous-titre */}
        <p className="fav-hero__sub">
          {favShops.length > 0
            ? `${favShops.length} magasin${favShops.length > 1 ? 's' : ''} sauvegardé${favShops.length > 1 ? 's' : ''}`
            : 'Vos magasins préférés apparaissent ici'}
        </p>

        {/* Stat cards statuts — uniquement si favoris */}
        {favShops.length > 0 && (
          <div className="fav-stats">

            <div className="fav-stat">
              <div className="fav-stat__row">
                <span className="fav-stat__dot fav-stat__dot--open" />
              </div>
              <div className="fav-stat__num">{counts.open}</div>
              <div className="fav-stat__lbl">Ouvert{counts.open > 1 ? 's' : ''}</div>
            </div>

            <div className="fav-stat">
              <div className="fav-stat__row">
                <span className="fav-stat__dot fav-stat__dot--closed" />
              </div>
              <div className="fav-stat__num">{counts.closed}</div>
              <div className="fav-stat__lbl">Fermé{counts.closed > 1 ? 's' : ''}</div>
            </div>

            <div className="fav-stat">
              <div className="fav-stat__row">
                <span className="fav-stat__dot fav-stat__dot--uncertain" />
              </div>
              <div className="fav-stat__num">{counts.uncertain}</div>
              <div className="fav-stat__lbl">Incertain{counts.uncertain > 1 ? 's' : ''}</div>
            </div>

          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          CONTENU
      ══════════════════════════════════════════ */}
      {favShops.length === 0 ? (

        /* ── État vide ── */
        <div className="fav-empty">
          <div className="fav-empty__icon-wrap">
            <div className="fav-empty__circle">
              <Heart size={30} strokeWidth={1.5} />
            </div>
            <span className="fav-empty__orb fav-empty__orb--1" />
            <span className="fav-empty__orb fav-empty__orb--2" />
            <span className="fav-empty__orb fav-empty__orb--3" />
          </div>

          <h3>Aucun favori pour l'instant</h3>

          <div className="fav-empty__tip">
            
            Appuyez sur ♥ sur la fiche d'un magasin pour les retrouver rapidement ici
          </div>
        </div>

      ) : (
        <>
          {/* ── Filtres par statut ── */}
          <div className="fav-filter-bar">
            {FILTERS.map(f => {
              const Icon = f.icon;
              return (
                <button
                  key={f.key}
                  className={`fav-chip${filter === f.key ? ' active' : ''}`}
                  onClick={() => setFilter(f.key)}
                  aria-pressed={filter === f.key}
                >
                  {f.dot && filter !== f.key && (
                    <span
                      className="fav-chip__dot"
                      style={{ background: f.dot }}
                    />
                  )}
                  {Icon && filter === f.key && (
                    <Icon size={12} strokeWidth={2.5} />
                  )}
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* ── Compteur résultats ── */}
          <p className="fav-count">
            {displayed.length} magasin{displayed.length > 1 ? 's' : ''}
            {filter !== 'all' ? ` · ${FILTERS.find(f => f.key === filter)?.label.toLowerCase()}` : ''}
          </p>

          {/* ── Liste ou vide filtré ── */}
          {displayed.length === 0 ? (
            <div className="empty-state" style={{ paddingTop: '2rem' }}>
              <div className="empty-state-icon">
                {filter === 'open'      && <CheckCircle size={28} strokeWidth={1.5} style={{ color: '#00c9a7' }} />}
                {filter === 'closed'    && <XCircle     size={28} strokeWidth={1.5} style={{ color: '#f0564a' }} />}
                {filter === 'uncertain' && <HelpCircle  size={28} strokeWidth={1.5} style={{ color: '#f5a623' }} />}
              </div>
              <h3>Aucun favori {FILTERS.find(f => f.key === filter)?.label.toLowerCase()}</h3>
              <p>Aucun de vos magasins sauvegardés n'est dans ce statut.</p>
            </div>
          ) : (
            <div className="shop-list" style={{ paddingTop: '.45rem' }}>
              {displayed.map(shop => (
                <ShopCard key={shop.id} shop={shop} />
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default Favorites;